import React, { useState, useEffect, useMemo } from "react";
import loader from "../../../assets/Images/loader.gif";
import { useNavigate } from "react-router-dom";

// --- Helper constants & functions ---
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]; 

const toDateObj = (str) => {
  if (!str) return null;
  return new Date(str.replace(" ", "T"));
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatDate = (dt) => `${pad2(dt.getDate())}-${MONTH_SHORT[dt.getMonth()]}`;

const formatTime = (dt) => {
  let h = dt.getHours();
  const m = pad2(dt.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

const formatRange = (startStr, endStr) => {
  const s = toDateObj(startStr);
  const e = toDateObj(endStr);
  if (!s || !e) return { date: "", range: "", duration: "" };
  const date = formatDate(s);
  const range = `${formatTime(s)} to ${formatTime(e)}`;
  const durationMin = Math.round((e - s) / 60000);
  return { date, range, duration: `${durationMin} Min` };
};

const statusBg = (statusRaw) => {
  const status = (statusRaw || "").toLowerCase();
 if (status === "open") return "#ffe6e6";         // light red
  if (status === "completed") return "#e6ffe6";     // light green
  return "#ffffff";
};

const BusinessShowAllMeetingsPG = () => {
  const [activeTab, setActiveTab] = useState("contact");
  const [loading, setLoading] = useState(true);
  const [contactData, setContactData] = useState([]);
  const [leadData, setLeadData] = useState([]);
  const [opportunityData, setOpportunityData] = useState([]);

  // today as YYYY-MM-DD
  const today = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
  }, []);
  const [selectedDate, setSelectedDate] = useState(today);

  useEffect(() => {
    const currentDate = new Date();
    const toDate = `${currentDate.getFullYear()}-${pad2(currentDate.getMonth() + 1)}-${pad2(currentDate.getDate())}`;

    const fromDateObj = new Date(currentDate);
    fromDateObj.setMonth(currentDate.getMonth() - 2);
    const formattedFromDate = `${fromDateObj.getFullYear()}-${pad2(fromDateObj.getMonth() + 1)}-${pad2(fromDateObj.getDate())}`;

    const fetchMeetings = async () => {
      setLoading(true);
      try {
        const requestData = {
          Authkey: "ac7b51de9d888e1458dd53d8aJAN3ba6f",
          Action: "geAlltMeeting",
          FromDate: formattedFromDate,
          ToDate: toDate,
          CurrentDate: "Yes",
          password: sessionStorage.getItem("password"),
          userName: sessionStorage.getItem("userName"),
        };
        const response = await fetch(process.env.REACT_APP_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });
        const data = await response.json();
        if (data.Status === "Success") {
          const { ContactData = [], LeadData = [], opportunityData = [] } = data.Response.Data || {};
          setContactData(ContactData);
          setLeadData(LeadData);
          setOpportunityData(opportunityData);
        } else {
          console.error("API Error:", data);
        }
      } catch (error) {
        console.error("Error fetching meetings:", error);
      }
      setLoading(false);
    };

    fetchMeetings();
  }, []);

  const navigate = useNavigate();
  const redirectBusinessPage = () => navigate("/businessCardListPG");

  const renderCard = (item, type) => {
    const { date, range, duration } = formatRange(item.MeetingDateandTime, item.MeetingEndDateandTime);
    return (
      <div className="card mt-3" style={{ backgroundColor: statusBg(item.MeetingStatus) }}>
        <div className="m-2 card-data">
          <p className="opportunity-card-title">{item.NameOfCompany}</p>
          <p className="opportunity-card-title">
            {type === "contact" ? item.ContactID : type === "lead" ? item.LeadID : item.OpportunityID}
          </p>
          <p className="opportunity-card-text">Date: {date}</p>
          <p className="opportunity-card-text">Time: {range}</p>
          <p className="opportunity-card-text">Duration: {duration}</p>
          <p className="opportunity-card-text">Mode: {item.MeetingMode || "N/A"}</p>
          <p className="opportunity-card-text">Status: {item.MeetingStatus || "N/A"}</p>
          <p className="opportunity-card-text">Met With: {item.MetWithWhom || "N/A"}</p>
          <p className="opportunity-card-text">Remarks: {item.Remarks || "N/A"}</p>
        </div>
      </div>
    );
  };

  const allMeetings = useMemo(() => [
    ...contactData.map(item => ({ ...item, __type: 'contact', __id: item.ContactID })),
    ...leadData.map(item => ({ ...item, __type: 'lead', __id: item.LeadID })),
    ...opportunityData.map(item => ({ ...item, __type: 'opportunity', __id: item.OpportunityID })),
  ], [contactData, leadData, opportunityData]);

  const filteredDayMeetings = useMemo(() =>
    allMeetings.filter(item => item.MeetingDateandTime?.split(' ')[0] === selectedDate)
  , [allMeetings, selectedDate]);

  const TabGrid = ({ data, type }) => (
    loading ? (
      <div className="loader-wrapper"><div className="loader"><img src={loader} alt="loading..."/></div></div>
    ) : (
      <div className="container-fluid">
        <div className="row row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-3">
          {data.length > 0 ? data.map(item => (
            <div className="col" key={item.__id}><>{renderCard(item, type)}</></div>
          )) : <p className="text-center mt-3">No {type} meetings found</p>}
        </div>
      </div>
    )
  );

  return (
    <>
      {/* Header */}
      <div className="container-fluid text-light Header-style header">
        <div className="p-3"><span className="p-1 m-3 h4">Appointments</span></div>
      </div>

      {/* Tabs navigation */}
      <div className="container-fluid text-light Header-style header">
        <div className="p-3">
          <span onClick={redirectBusinessPage} style={{ cursor: 'pointer' }}><i className="fa-solid fa-arrow-left-long"></i></span>
          <span className="p-1 m-3 h4">Meeting Details</span>
        </div>
        <ul className="nav nav-tabs">
          {['contact','lead','opportunity','day'].map(tabKey => (
            <li className="nav-item" key={tabKey}>
              <a className={`nav-link ${activeTab===tabKey?'active':''}`} onClick={()=>setActiveTab(tabKey)}>
                {tabKey==='day'?'Scheduled Meetings':tabKey.charAt(0).toUpperCase()+tabKey.slice(1)}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Tab Contents */}
      <div className="tab-content tab-main-content">
        <div className={`tab-pane fade ${activeTab==='contact'?'show active':''}`}><TabGrid data={contactData} type="contact"/></div>
        <div className={`tab-pane fade ${activeTab==='lead'?'show active':''}`}><TabGrid data={leadData} type="lead"/></div>
        <div className={`tab-pane fade ${activeTab==='opportunity'?'show active':''}`}><TabGrid data={opportunityData} type="opportunity"/></div>
        <div className={`tab-pane fade ${activeTab==='day'?'show active':''}`}>  
          {loading ? <div className="loader-wrapper"><div className="loader"><img src={loader} alt="loading..."/></div></div> : (
            <div className="container-fluid">
              <div className="my-3">
                <label htmlFor="meetingDate">Select date:</label>
                <input type="date" id="meetingDate" className="form-control form-control-sm mt-1"
                  value={selectedDate} onChange={e=>setSelectedDate(e.target.value)} />
              </div>
              <div className="row row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-3">
                {filteredDayMeetings.length>0 ? filteredDayMeetings.map(item=>(
                  <div className="col" key={item.__id}>{renderCard(item,item.__type)}</div>
                )) : <p className="text-center mt-3">No meetings scheduled for {selectedDate}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BusinessShowAllMeetingsPG;
